import React, { useState, useRef, useEffect } from "react";
import LivePostLink from "./LivePostLink";
import ImageMediaComponent from "./ImageMediaComponent";
import VideoMediaComponent from "./VideoMediaComponent";
import CarouselSlideStrip from "../Shared/CarouselSlideStrip";
import { Platform } from "../../types/Platforms";
import { Post } from "../../types/postTypes";
import { ConnectedProfile } from "../../types/customer";
import { isVideoPlatform } from "../../utils/platformUtils";
import { getConnectedLinkedInTargets, mapLinkedInPlatform } from "../../utils/allowedPlatformMapping";
import ScheduleDateTimeField from "../Shared/ScheduleDateTimeField";
import DocumentPagesPreview from "./DocumentPagesPreview";
import { formatInTimezone, resolveScheduleTimezone } from "../../utils/scheduleTime";
import { useActiveScheduleTimezone } from "../../hooks/useScheduleTimezone";
import TikTokPostSettings, { TikTokConsentNotice } from "../../features/tiktok/components/TikTokPostSettings";
import { useTikTokCreatorInfo } from "../../features/tiktok/hooks/useTikTokCreatorInfo";
import type { TikTokOptions } from "../../features/tiktok/types";
import {
  TIKTOK_INBOX_MESSAGE,
  TIKTOK_INBOX_APPROVE_TOOLTIP,
  TIKTOK_PROCESSING_MESSAGE,
  isTikTokPlatform,
  missingTikTokRequiredFields,
} from "../../features/tiktok/utils/tiktokOptions";

const FORMAT_OPTIONS: {
  value: "image" | "video" | "carousel";
  label: string;
  icon: string;
}[] = [
  { value: "carousel", label: "Carousel", icon: "view_carousel" },
  { value: "image", label: "Image", icon: "image" },
  { value: "video", label: "Video", icon: "movie" },
];

/** LinkedIn PDF document/carousel (SGL-174): recover a readable filename from the S3/CDN URL. */
function pdfFileNameFromUrl(url: string, index: number): string {
  try {
    const path = new URL(url).pathname;
    return decodeURIComponent(path.split("/").pop() || `document-${index + 1}.pdf`);
  } catch {
    return `document-${index + 1}.pdf`;
  }
}

interface PostPreviewCardFullProps {
  onRegenerateCaption?: (post: Post) => void;
  post: Post;
  isSelected: boolean;
  onSelect: (postId: string, post: { customer_id: string; post_id: string }) => void;
  editablePostId: string | null;
  setEditablePostId: (postId: string | null) => void;
  editableTime: Date | null;
  setEditableTime: (date: Date | null) => void;
  handleSaveTime: (
    postId: string,
    newTime: Date | null,
    timezone?: string
  ) => void;
  editableCaptionPostId: string | null;
  setEditableCaptionPostId: (postId: string | null) => void;
  editableContent: string;
  setEditableContent: (content: string) => void;
  handleSaveCaption: (postId: string) => void;
  editableHashtagsPostId: string | null;
  setEditableHashtagsPostId: (postId: string | null) => void;
  editableHashtags: string | null;
  setEditableHashtags: (hashtags: string) => void;
  handleSaveHashtags: (postId: string) => void;
  editableMediaPostId: string | null;
  enableEditMedia: (postId: string) => void;
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    postId: string
  ) => void;
  handleSaveMedia: (postId: string, mediaType: "image" | "video", directFile?: File) => void;
  handleRemoveMedia?: (postId: string, mediaType: "image" | "video") => void;
  cancelEditMedia: (postId: string) => void;
  selectedFile: File | null;
  handleApprove: (postId: string) => void;
  /**
   * SGL-501: show a PENDING post's time as a "planned" time (becomes the schedule on
   * approval) and let it be edited before approval. Opt-in, default false: only the
   * review list (ReviewPost) honours a planned time on approve. Other callers
   * (CreatePostComponentV2) keep the original labels and TikTok lock. When on,
   * posts that are neither PENDING nor APPROVED cannot have a time edited.
   */
  plannedTimeForPending?: boolean;
  handlePublishPost: (postId: string, customerId: string) => void;
  publishingPostId: string | null;
  publishError: { [key: string]: string };
  editableCallToActionPostId: string | null;
  setEditableCallToActionPostId: (postId: string | null) => void;
  uploadingMediaPostId?: string | null;
  editableCallToAction: string;
  setEditableCallToAction: (val: string) => void;
  handleSaveCallToAction: (postId: string) => void;
  editableBusinessProfileIdPostId: string | null;
  setEditableBusinessProfileIdPostId: (postId: string | null) => void;
  editableBusinessProfileId: string;
  setEditableBusinessProfileId: (val: string) => void;
  handleSaveBusinessProfileId: (postId: string, overrideValue?: string) => void;
  /** For the LinkedIn Personal/Business Page toggle. Omitted (e.g. the legacy
   * create-preview path) simply falls back to the raw account-id editor. */
  connectedProfiles?: ConnectedProfile[];
  editableTitlePostId: string | null;
  setEditableTitlePostId: (postId: string | null) => void;
  editableTitle: string;
  setEditableTitle: (val: string) => void;
  handleSaveTitle: (postId: string, customerId: string) => void;
  regeneratingPostId?: string | null;
  regenerationSuccess?: string | null;
  handleDeletePost: (postId: string, customerId: string) => void;
  handleSavePrivacy?: (postId: string, privacy: string) => void;
  handleSaveMadeForKids?: (postId: string, madeForKids: boolean) => void;
  /** TikTok (SGL-170): persist tiktok_options (and the open_id as business_profile_id). Omitted = settings panel not shown and TikTok publish stays blocked. */
  handleSaveTikTokOptions?: (postId: string, options: TikTokOptions) => void | Promise<void>;
  /** TikTok (SGL-170): the connected profile's open_id the post publishes to. */
  tiktokProfileId?: string;
  /** Session token for the TikTok creator-info lookup. */
  idToken?: string;
  isSaving?: boolean;
  approvingPostId?: string | null;
  /** Carousel: remove a slide by index */
  handleRemoveCarouselSlide?: (postId: string, index: number) => void;
  /** Carousel: reorder slides via drag */
  handleReorderCarouselSlides?: (postId: string, fromIndex: number, toIndex: number) => void;
  /** Carousel: add multiple slides */
  handleAddCarouselSlides?: (postId: string, files: FileList | File[]) => Promise<void>;
  /** Format change */
  /** Resolves false when the change could not be saved (callers may roll back local state). */
  handleFormatChange?: (
    postId: string,
    newFormat: "image" | "video" | "carousel"
  ) => void | boolean | Promise<void | boolean>;
  /** Open Media Library */
  handleOpenLibrary?: (postId: string, format: "image" | "video" | "carousel") => void;
}

const PostPreviewCardFull = ({
  post,
  isSelected,
  onSelect,
  editablePostId,
  setEditablePostId,
  editableTime,
  setEditableTime,
  handleSaveTime,
  editableCaptionPostId,
  setEditableCaptionPostId,
  editableContent,
  setEditableContent,
  handleSaveCaption,
  editableHashtagsPostId,
  setEditableHashtagsPostId,
  editableHashtags,
  setEditableHashtags,
  handleSaveHashtags,
  editableMediaPostId,
  enableEditMedia,
  handleFileChange,
  handleSaveMedia,
  handleRemoveMedia,
  cancelEditMedia,
  selectedFile,
  handleApprove,
  plannedTimeForPending = false,
  handlePublishPost,
  publishingPostId,
  publishError,
  onRegenerateCaption,
  editableCallToActionPostId,
  setEditableCallToActionPostId,
  editableCallToAction,
  setEditableCallToAction,
  handleSaveCallToAction,
  editableBusinessProfileIdPostId,
  setEditableBusinessProfileIdPostId,
  editableBusinessProfileId,
  setEditableBusinessProfileId,
  handleSaveBusinessProfileId,
  editableTitlePostId,
  setEditableTitlePostId,
  editableTitle,
  setEditableTitle,
  handleSaveTitle,
  regeneratingPostId,
  regenerationSuccess,
  handleDeletePost,
  uploadingMediaPostId = null,
  handleSavePrivacy,
  handleSaveMadeForKids,
  handleSaveTikTokOptions,
  tiktokProfileId,
  idToken,
  isSaving = false,
  approvingPostId = null,
  handleRemoveCarouselSlide,
  handleReorderCarouselSlides,
  handleAddCarouselSlides,
  handleFormatChange,
  handleOpenLibrary,
  connectedProfiles = [],
}: PostPreviewCardFullProps) => {
  const isUploading = uploadingMediaPostId === post.post_id;

  // Timezone the schedule editor works in: the post's own zone when it has one,
  // otherwise the business profile's. Kept local to the card so editing one
  // post's zone never disturbs another's.
  const { timezone: profileTimezone, isResolved: timezoneResolved } = useActiveScheduleTimezone();
  const [editTimezone, setEditTimezone] = useState<string>(() =>
    resolveScheduleTimezone(post.scheduled_timezone || profileTimezone)
  );
  // True once the user has explicitly selected a timezone in the picker.
  // Until then, the editor should adopt the business profile zone as soon as
  // it loads — even if the editor panel is already open.
  const userAdjustedTimezoneRef = React.useRef(false);

  useEffect(() => {
    if (post.scheduled_timezone) {
      // Always honour a zone the post already has saved.
      setEditTimezone(resolveScheduleTimezone(post.scheduled_timezone));
      return;
    }
    // Adopt the profile default once it has resolved from the API, unless
    // the user has already made an explicit choice in the picker this session.
    if (timezoneResolved && !userAdjustedTimezoneRef.current) {
      setEditTimezone(resolveScheduleTimezone(profileTimezone));
    }
  }, [post.scheduled_timezone, profileTimezone, timezoneResolved]);

  // Reset the "user adjusted" flag when the editor is closed, so reopening
  // it for a different post always starts fresh from the profile timezone.
  useEffect(() => {
    if (editablePostId !== post.post_id) {
      userAdjustedTimezoneRef.current = false;
    }
  }, [editablePostId, post.post_id]);

  // The single zone every read-only render of this post's schedule uses.
  //
  // Posts written before `scheduled_timezone` existed — and any row whose
  // batch-create dropped it — have no stored zone. Those must still render on
  // the BUSINESS profile's clock, never the viewer's: falling back to
  // browser-local made a Chicago post read "12:40 PM GMT+8" to a viewer in
  // Manila while the caption underneath still said "Chicago".
  const displayTimezone = resolveScheduleTimezone(
    post.scheduled_timezone || editTimezone
  );

  const platformIsInstagram = post.platform?.toUpperCase().includes("INSTAGRAM");
  /** LinkedIn also accepts a native video post (SGL-301) alongside its existing
   * image/PDF-document/PDF-carousel formats — unlike Instagram it has no separate
   * "carousel" format wired to this dropdown (LinkedIn's carousel is PDF-based,
   * driven by carousel_slides/document_url, not the image carousel_media this
   * dropdown's "carousel" option writes), so only image/video are offered. */
  const platformIsLinkedIn = post.platform?.toUpperCase() === "LINKEDIN";
  /** Facebook has no format field on the post (unlike Instagram's instagram_content_format),
   * so a picked format is remembered locally until media is added — an empty carousel
   * is otherwise indistinguishable from an empty image post. */
  const platformIsFacebook = post.platform?.toUpperCase() === "FACEBOOK";
  const [facebookPickedFormat, setFacebookPickedFormat] = useState<"image" | "video" | "carousel" | null>(null);
  const formatOptionsForPlatform = platformIsLinkedIn
    ? FORMAT_OPTIONS.filter((o) => o.value !== "carousel")
    : FORMAT_OPTIONS;
  /** LinkedIn Reels shares its connected profile with LinkedIn (SGL-301) —
   * unlike the format dropdown above, the Personal/Business Page toggle
   * applies to both. */
  const platformIsLinkedInFamily = Boolean(post.platform?.toUpperCase().startsWith("LINKEDIN"));
  const linkedInTargets = platformIsLinkedInFamily
    ? getConnectedLinkedInTargets(connectedProfiles)
    : { personal: false, business: false };
  const showLinkedInTargetToggle =
    platformIsLinkedInFamily && (linkedInTargets.personal || linkedInTargets.business);
  const effectiveLinkedInTarget: "personal" | "business" = (() => {
    const bid = post.business_profile_id || "";
    if (bid.startsWith("urn:li:organization:")) return "business";
    if (bid && !bid.toLowerCase().startsWith("default_")) return "personal";
    const auto = mapLinkedInPlatform(post.platform, connectedProfiles);
    return auto.business_profile_id.startsWith("urn:li:organization:") ? "business" : "personal";
  })();
  /** Mirrors canvas `getInstagramFormat`: explicit format wins, then infer from post_type / slides. */
  const igContentFormat: "image" | "video" | "carousel" | undefined = platformIsInstagram
    ? post.instagram_content_format ??
      (post.post_type === "CAROUSEL"
        ? "carousel"
        : post.post_type === "SINGLE_VIDEO"
          ? "video"
          : post.carousel_media?.length
            ? "carousel"
            : "image")
    : undefined;

  const facebookFormat: "image" | "video" | "carousel" | undefined = platformIsFacebook
    ? facebookPickedFormat ??
      (post.post_type === "CAROUSEL" ||
      (post.post_type !== "SINGLE_IMAGE" &&
        post.post_type !== "SINGLE_VIDEO" &&
        Boolean(post.carousel_media?.length))
        ? "carousel"
        : post.post_type === "SINGLE_VIDEO" || Boolean(post.video_url?.trim())
          ? "video"
          : "image")
    : undefined;

  const isCarousel = platformIsFacebook
    ? facebookFormat === "carousel"
    : post.post_type === "CAROUSEL" ||
      (platformIsInstagram && igContentFormat === "carousel") ||
      (!platformIsInstagram &&
        post.post_type !== "SINGLE_IMAGE" &&
        post.post_type !== "SINGLE_VIDEO" &&
        Array.isArray(post.carousel_media) &&
        post.carousel_media.length > 0);

  /** Feed platforms (e.g. Instagram) are not in VIDEO_PLATFORMS; still show a video when `video_url` is set. */
  const hasVideoAttachment = Boolean(post.video_url?.trim());
  const showVideoPreview =
    !isCarousel &&
    (facebookFormat === "video" ||
      (facebookFormat !== "image" && hasVideoAttachment) ||
      post.post_type === "SINGLE_VIDEO" ||
      isVideoPlatform(post.platform as Platform));

  /** LinkedIn PDF carousel (SGL-174): 2+ PDFs merged server-side into one document. */
  const pdfCarouselSlides = (post.carousel_slides || []).filter((s) => s?.url);
  const isPdfCarousel = !isCarousel && pdfCarouselSlides.length > 0;
  /** LinkedIn single document post (SGL-174): one PDF, rendered natively by LinkedIn. */
  const hasDocumentAttachment = !isPdfCarousel && Boolean(post.document_url?.trim());
  /** The PDF file tile: off LinkedIn, and on LinkedIn when its pages can't be drawn (SGL-573). */
  const pdfTile = () =>
    isPdfCarousel ? (
      <div className="flex w-full flex-col items-center gap-3 rounded-lg border border-theme-border bg-theme-background p-4">
        <span className="material-icons-outlined text-5xl text-[#0A66C2]">auto_stories</span>
        <span className="rounded bg-[#0A66C2]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[#0A66C2]">
          PDF Carousel · {pdfCarouselSlides.length} files
        </span>
        <span className="text-center text-xs text-theme-fg-muted">
          Merged into one document carousel on LinkedIn
        </span>
        <div className="flex w-full max-w-xs flex-col gap-1.5">
          {pdfCarouselSlides.map((slide, idx) => (
            <a
              key={idx}
              href={slide.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-md border border-theme-border bg-theme-surface px-2.5 py-1.5 text-xs font-medium text-brand-primary underline transition-colors hover:bg-theme-secondary/10"
              title={pdfFileNameFromUrl(slide.url, idx)}
            >
              <span className="material-icons-outlined shrink-0 text-sm text-red-500">picture_as_pdf</span>
              <span className="truncate">{pdfFileNameFromUrl(slide.url, idx)}</span>
            </a>
          ))}
        </div>
      </div>
    ) : (
      <div className="flex w-full flex-col items-center gap-3 rounded-lg border border-theme-border bg-theme-background p-4">
        <span className="material-icons-outlined text-5xl text-red-500">picture_as_pdf</span>
        <span className="rounded bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
          PDF Document
        </span>
        <a
          href={post.document_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex w-full max-w-xs items-center gap-1.5 rounded-md border border-theme-border bg-theme-surface px-2.5 py-1.5 text-xs font-medium text-brand-primary underline transition-colors hover:bg-theme-secondary/10"
          title={pdfFileNameFromUrl(post.document_url!, 0)}
        >
          <span className="material-icons-outlined shrink-0 text-sm text-red-500">picture_as_pdf</span>
          <span className="truncate">{pdfFileNameFromUrl(post.document_url!, 0)}</span>
        </a>
      </div>
    );

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);
  const formatMenuRef = useRef<HTMLDivElement>(null);
  const carouselFileInputRef = React.useRef<HTMLInputElement>(null);

  // Click outside detection for format dropdown
  useEffect(() => {
    if (!formatMenuOpen) return;
    const close = (e: MouseEvent) => {
      if (formatMenuRef.current && !formatMenuRef.current.contains(e.target as Node)) {
        setFormatMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [formatMenuOpen]);

  useEffect(() => {
    if (isCarousel && (!post.carousel_media || post.carousel_media.length === 0)) {
      setActiveSlideIndex(0);
    }
  }, [isCarousel, post.carousel_media?.length]);

  useEffect(() => {
    if ((post.carousel_media?.length ?? 0) > 0) setShowAddMenu(false);
  }, [post.carousel_media?.length]);

  const formatScheduledDisplay = (value?: string) => {
    if (!value) return isPending ? "No planned time" : "Not scheduled";
    const d = new Date(value);
    const showYear = !Number.isNaN(d.getTime()) && d.getFullYear() !== new Date().getFullYear();
    return (
      formatInTimezone(value, displayTimezone, {
        month: "short",
        day: "numeric",
        ...(showYear ? { year: "numeric" } : {}),
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        // The city caption underneath already names the clock; the "CDT"/"GMT+8"
        // suffix formatInTimezone adds by default is redundant noise here.
        timeZoneName: undefined,
      }) || value
    );
  };

  // "America/Chicago" → "Chicago", "America/New_York" → "New York"
  const formatTimezoneCity = (tz?: string): string => {
    if (!tz) return "";
    const city = tz.split("/").pop() ?? tz;
    return city.replace(/_/g, " ");
  };


  const getPlatformDisplayName = (platform: string): string => {
    const key = platform.trim().toUpperCase().replace(/\s+/g, "_");
    const names: Record<string, string> = {
      FACEBOOK: "Facebook",
      INSTAGRAM: "Instagram",
      LINKEDIN: "LinkedIn",
      YOUTUBE: "YouTube",
      YOUTUBE_SHORTS: "YouTube",
      GMB: "Google Business",
      TWITTER: "X",
      X: "X",
      TIKTOK: "TikTok",
      PINTEREST: "Pinterest",
    };
    if (names[key]) return names[key];
    if (!key) return "Account";
    return key
      .split("_")
      .map((seg) => seg.charAt(0) + seg.slice(1).toLowerCase())
      .join(" ");
  };

  const getAccountIdFieldLabel = (platform: string): string =>
    `${getPlatformDisplayName(platform)} Account ID`;

  const isDefaultBusinessProfileId = (id: string | undefined | null): boolean =>
    (id ?? "").toLowerCase().startsWith("default_");

  // Get platform-specific label for caption/description
  const getCaptionLabel = (platform: string): string => {
    const platformUpper = platform.toUpperCase();
    if (platformUpper === "YOUTUBE" || platformUpper === "YOUTUBE_SHORTS")
      return "Description";
    return "Caption";
  };

  const captionLabel = getCaptionLabel(post.platform);

  // Get lowercase version for use in messages
  const captionLabelLower = captionLabel.toLowerCase();

  const hashtagTagsForDisplay = (post.hashtags || "")
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const statusUpper = post.status?.toUpperCase() ?? "";
  const isApproved = statusUpper === "APPROVED";
  const isYouTubePlatform =
    post.platform?.toUpperCase() === "YOUTUBE" ||
    post.platform?.toUpperCase() === "YOUTUBE_SHORTS";
  const missingYouTubeRequiredFields =
    isYouTubePlatform &&
    (!post.privacy_status || typeof post.made_for_kids !== "boolean");
  // TikTok Direct Post (SGL-170): the creator, privacy, disclosure and duration
  // rules gate Publish and Schedule exactly like the YouTube fields do.
  const isTikTokPost = isTikTokPlatform(post.platform);
  const tiktokCreator = useTikTokCreatorInfo({
    customerId: post.customer_id,
    profileId: tiktokProfileId,
    idToken,
    enabled: isTikTokPost && !!handleSaveTikTokOptions,
  });
  const [tiktokVideoDuration, setTiktokVideoDuration] = useState<number | null>(null);
  const tiktokProblems = isTikTokPost
    ? missingTikTokRequiredFields(
        post.tiktok_options,
        tiktokCreator.creatorInfo,
        tiktokVideoDuration,
        !!post.video_url
      )
    : [];
  const missingTikTokFields = tiktokProblems.length > 0;
  const isSentToTikTok = statusUpper === "SENT_TO_TIKTOK";
  // ENQUEUED = the publisher has the row and TikTok is still processing the video.
  const isTikTokProcessing = isTikTokPost && statusUpper === "ENQUEUED";
  // "Can take a few minutes to appear" only applies right after publishing: a
  // PUBLISHED row with no platform_post_id whose last update was under 30 minutes ago
  // (the session's own publish stamps updated_at, so it qualifies too).
  const updatedAtMs = Date.parse(post.updated_at || "");
  const showTikTokProcessingNote =
    isTikTokPost &&
    statusUpper === "PUBLISHED" &&
    !post.platform_post_id &&
    Number.isFinite(updatedAtMs) &&
    Date.now() - updatedAtMs < 30 * 60 * 1000;
  const isPublished = statusUpper === "PUBLISHED";
  const publishDisabled =
    publishingPostId === post.post_id ||
    isPublished ||
    isSentToTikTok ||
    isTikTokProcessing ||
    !isApproved ||
    missingYouTubeRequiredFields ||
    missingTikTokFields;

  const formatScheduledBadge = (iso: string): string =>
    formatInTimezone(iso, displayTimezone, {
      month: "short",
      day: "numeric",
      ...(new Date(iso).getFullYear() !== new Date().getFullYear()
        ? { year: "numeric" }
        : {}),
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: undefined,
    });

  const isScheduledApproved =
    isApproved && !!post.scheduled_time?.trim() && !!formatScheduledBadge(post.scheduled_time);

  // SGL-501: a PENDING post can carry a *planned* time. The scheduler only picks up
  // APPROVED rows, so the planned time does nothing until the owner approves; it is
  // therefore never labelled "Scheduled" while the post is pending.
  // Opt-in (ReviewPost only): other callers keep the original, non-planned behaviour.
  const isPending = plannedTimeForPending && statusUpper === "PENDING";
  // Without approval only PENDING (planned) and APPROVED posts can have a time set.
  const scheduleLockedPendingApproval =
    plannedTimeForPending && !isApproved && statusUpper !== "PENDING";
  const plannedMs = post.scheduled_time?.trim() ? new Date(post.scheduled_time).getTime() : NaN;
  const isPlannedPending =
    isPending && !Number.isNaN(plannedMs) && !!formatScheduledBadge(post.scheduled_time);
  const plannedTimePassed = isPlannedPending && plannedMs <= Date.now();
  // A TikTok schedule records consent to the settings above, so they must be valid first.
  const scheduleLockedTikTok = isTikTokPost && missingTikTokFields && !isPending;

  const getPostStatusLabelForUi = (status: string): string => {
    if (status?.toUpperCase() === "APPROVED") {
      if (isScheduledApproved) {
        return `Scheduled ${formatScheduledBadge(post.scheduled_time)}`;
      }
      return "Approved. Ready to Post.";
    }
    if (status?.toUpperCase() === "PENDING" && isPlannedPending) {
      return `Planned: ${formatScheduledBadge(post.scheduled_time)} · goes out once approved`;
    }
    if (status?.toUpperCase() === "SENT_TO_TIKTOK") return "Sent to TikTok";
    return status;
  };
  const canEditMedia = post.status !== "PUBLISHED" && post.status !== "SCHEDULED";

  return (
    <div className="relative mx-auto flex max-w-5xl flex-col overflow-hidden rounded-xl border border-theme-border bg-theme-surface p-4 shadow-sm md:flex-row">
      <div className="absolute right-4 top-4 z-10">
        <input
          type="checkbox"
          className="h-5 w-5 rounded border-theme-border text-brand-primary-light focus:ring-brand-primary-light"
          checked={isSelected}
          onChange={() => onSelect(post.post_id, { customer_id: post.customer_id, post_id: post.post_id })}
          id={`post-selection-${post.post_id}`}
        />
      </div>
      {/* Media Section */}
      <div className={`relative flex flex-col w-full flex-shrink-0 items-center justify-start rounded-lg border border-theme-border bg-theme-background p-1 md:w-2/5 ${!isCarousel ? 'overflow-hidden' : ''}`}>
        {/* Format Selection - Space-saving dropdown at the top */}
        {canEditMedia && handleFormatChange && (platformIsInstagram || platformIsLinkedIn || platformIsFacebook) && (
          <div className="z-30 mb-3 w-full border-b border-theme-border/50 px-4 pb-4 pt-3">
            <label className="mb-2 block text-[10px] font-bold uppercase tracking-wider text-theme-fg-muted">
              Content format
            </label>
            <div className="relative" ref={formatMenuRef}>
              <button
                type="button"
                onClick={() => setFormatMenuOpen((o) => !o)}
                className="flex w-full items-center gap-3 rounded-xl border border-theme-border bg-theme-surface px-3 py-2.5 text-left shadow-sm outline-none ring-[#2f4941]/30 transition-colors hover:border-theme-border hover:bg-theme-background/80 focus:border-brand-primary focus:ring-2"
                aria-expanded={formatMenuOpen}
              >
                {(() => {
                  const currentFmtValue =
                    igContentFormat ??
                    facebookFormat ??
                    (isCarousel
                      ? "carousel"
                      : post.post_type === "SINGLE_VIDEO" ||
                          hasVideoAttachment ||
                          isVideoPlatform(post.platform as Platform)
                        ? "video"
                        : "image");
                  const meta =
                    formatOptionsForPlatform.find((o) => o.value === currentFmtValue) ??
                    formatOptionsForPlatform.find((o) => o.value === "image") ??
                    formatOptionsForPlatform[0];
                  return (
                    <>
                      <span className="material-icons text-[20px] text-theme-fg-muted">{meta.icon}</span>
                      <span className="flex-1 text-sm font-semibold text-theme-fg">{meta.label}</span>
                      <span className="material-icons text-theme-fg-muted">
                        {formatMenuOpen ? "expand_less" : "expand_more"}
                      </span>
                    </>
                  );
                })()}
              </button>

              {formatMenuOpen && (
                <ul className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-xl border border-theme-border bg-theme-surface py-1 shadow-lg animate-in fade-in zoom-in-95">
                  {formatOptionsForPlatform.map((opt) => {
                    const isActive =
                      platformIsInstagram && igContentFormat
                        ? opt.value === igContentFormat
                        : facebookFormat
                          ? opt.value === facebookFormat
                          : (opt.value === "carousel" && isCarousel) ||
                          (opt.value === "video" &&
                            !isCarousel &&
                            (post.post_type === "SINGLE_VIDEO" ||
                              hasVideoAttachment ||
                              isVideoPlatform(post.platform as Platform))) ||
                          (opt.value === "image" &&
                            !isCarousel &&
                            post.post_type !== "SINGLE_VIDEO" &&
                            !hasVideoAttachment &&
                            !isVideoPlatform(post.platform as Platform));

                    return (
                      <li key={opt.value}>
                        <button
                          type="button"
                          onClick={async () => {
                            setFormatMenuOpen(false);
                            if (!platformIsFacebook) {
                              handleFormatChange(post.post_id, opt.value);
                              return;
                            }
                            // Optimistic, so an empty carousel shows its dropzone at once;
                            // undone if the save fails so the card matches what is stored.
                            const previous = facebookPickedFormat;
                            setFacebookPickedFormat(opt.value);
                            const saved = await handleFormatChange(post.post_id, opt.value);
                            if (saved === false) setFacebookPickedFormat(previous);
                          }}
                          className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm font-medium transition-colors hover:bg-theme-secondary/10 ${
                            isActive ? "bg-[#2f4941]/10 text-brand-primary" : "text-theme-fg"
                          }`}
                        >
                          <span className={`material-icons text-[20px] ${isActive ? "text-brand-primary" : "text-theme-fg-muted"}`}>
                            {opt.icon}
                          </span>
                          {opt.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Loading Overlay - Shows during upload */}
        {isUploading && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="relative">
              <div className="h-16 w-16 animate-spin rounded-full border-4 border-white/30 border-t-brand-primary-light" />
            </div>
            <p className="mt-4 text-lg font-semibold text-white">
              Uploading...
            </p>
            <p className="mt-1 text-sm text-white/80">Please wait</p>
          </div>
        )}

        {isCarousel ? (
          <div className="flex w-full flex-col">
            {post.carousel_media && post.carousel_media.length > 0 ? (
              <CarouselSlideStrip
                slides={post.carousel_media}
                platformLabel={platformIsFacebook ? "Facebook" : "Instagram"}
                activeIndex={activeSlideIndex}
                onActiveIndexChange={setActiveSlideIndex}
                onRemoveSlide={
                  handleRemoveCarouselSlide && canEditMedia
                    ? (index) => handleRemoveCarouselSlide(post.post_id, index)
                    : undefined
                }
                onRemoveHero={
                  handleRemoveCarouselSlide && canEditMedia
                    ? () => handleRemoveCarouselSlide(post.post_id, activeSlideIndex)
                    : undefined
                }
                onReorder={
                  handleReorderCarouselSlides && canEditMedia
                    ? (from, to) => handleReorderCarouselSlides(post.post_id, from, to)
                    : undefined
                }
                addSlideContent={
                  handleAddCarouselSlides && canEditMedia ? (
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowAddMenu((prev) => !prev)}
                        className="group flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border-2 border-dashed border-theme-border bg-theme-surface text-theme-fg-muted transition-all hover:border-brand-primary hover:bg-[#2f4941]/5 hover:text-brand-primary"
                      >
                        <span className="material-icons transition-transform group-hover:scale-125 group-hover:drop-shadow-sm">
                          add
                        </span>
                      </button>
                      {showAddMenu && (
                        <>
                          <div
                            className="fixed inset-0 z-40"
                            onClick={() => setShowAddMenu(false)}
                          />
                          <div className="absolute left-full top-0 ml-2 z-50 flex w-48 flex-col rounded-xl border border-theme-border bg-theme-surface p-1.5 shadow-xl ring-1 ring-black/5 animate-in fade-in zoom-in-95 data-[side=top]:slide-in-from-bottom-2">
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddMenu(false);
                                handleOpenLibrary?.(post.post_id, "carousel");
                              }}
                              className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-theme-fg hover:bg-theme-secondary/10"
                            >
                              <span className="material-icons text-[18px] text-brand-primary">photo_library</span>
                              My Library
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddMenu(false);
                                carouselFileInputRef.current?.click();
                              }}
                              className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-theme-fg hover:bg-theme-secondary/10"
                            >
                              <span className="material-icons text-[18px] text-theme-fg-muted">upload_file</span>
                              Local files
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : undefined
                }
              />
            ) : (
              <div className="flex w-full flex-col gap-3 px-1">
                {canEditMedia && (handleOpenLibrary || handleAddCarouselSlides) ? (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowAddMenu((prev) => !prev)}
                      className="group flex aspect-square w-full flex-col items-center justify-center gap-2 overflow-hidden rounded-lg border-2 border-dashed border-theme-border bg-theme-background/30 px-4 text-center outline-none transition-colors hover:border-brand-primary hover:bg-[#2f4941]/5 focus-visible:border-brand-primary focus-visible:ring-2 focus-visible:ring-[#2f4941]/30"
                    >
                      <span className="material-icons text-4xl text-theme-fg-muted transition-transform group-hover:scale-105">
                        view_carousel
                      </span>
                      <p className="text-[13px] font-semibold text-theme-fg-muted">
                        {platformIsFacebook ? "Add photos (2+ for a Facebook carousel)" : "Add slides (2+ for a published Instagram carousel)"}
                      </p>
                    </button>
                    {showAddMenu && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setShowAddMenu(false)}
                          aria-hidden
                        />
                        <div className="absolute left-1/2 top-full z-50 mt-2 w-48 -translate-x-1/2 flex flex-col rounded-xl border border-theme-border bg-theme-surface p-1.5 shadow-xl ring-1 ring-black/5 animate-in fade-in zoom-in-95">
                          {handleOpenLibrary && (
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddMenu(false);
                                handleOpenLibrary(post.post_id, "carousel");
                              }}
                              className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-theme-fg hover:bg-theme-secondary/10"
                            >
                              <span className="material-icons text-[18px] text-brand-primary">photo_library</span>
                              My Library
                            </button>
                          )}
                          {handleAddCarouselSlides && (
                            <button
                              type="button"
                              onClick={() => {
                                setShowAddMenu(false);
                                carouselFileInputRef.current?.click();
                              }}
                              className="flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-theme-fg hover:bg-theme-secondary/10"
                            >
                              <span className="material-icons text-[18px] text-theme-fg-muted">upload_file</span>
                              Local files
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="relative aspect-square w-full overflow-hidden rounded-lg border-2 border-dashed border-theme-border bg-theme-background/30">
                    <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
                      <span className="material-icons text-4xl text-theme-fg-muted">view_carousel</span>
                      <p className="text-[13px] font-semibold text-theme-fg-muted">
                        {platformIsFacebook ? "Add photos (2+ for a Facebook carousel)" : "Add slides (2+ for a published Instagram carousel)"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
            {handleAddCarouselSlides && canEditMedia && (
              <input
                ref={carouselFileInputRef}
                type="file"
                multiple
                accept={platformIsFacebook ? "image/*" : "image/*,video/*"}
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    // Copy first: resetting the input below empties the live FileList mid-upload.
                    handleAddCarouselSlides(post.post_id, Array.from(e.target.files));
                    e.target.value = ""; // reset
                  }
                }}
              />
            )}
          </div>
        ) : showVideoPreview ? (
          <VideoMediaComponent
            videoUrl={post.video_url}
            onOpenLibrary={canEditMedia && handleOpenLibrary ? () => handleOpenLibrary(post.post_id, "video") : undefined}
            onUploadLocal={canEditMedia && handleSaveMedia ? (file) => handleSaveMedia(post.post_id, "video", file) : undefined}
            isUploading={isUploading}
          />
        ) : (isPdfCarousel || hasDocumentAttachment) && platformIsLinkedIn ? (
          // SGL-573: the PDF's pages, as LinkedIn will show them; the PDF tile if they can't be drawn.
          <DocumentPagesPreview
            customerId={post.customer_id}
            urls={isPdfCarousel ? pdfCarouselSlides.map((s) => s.url) : [post.document_url!.trim()]}
            idToken={idToken}
            fallback={pdfTile()}
          />
        ) : isPdfCarousel || hasDocumentAttachment ? (
          pdfTile()
        ) : (
          <ImageMediaComponent
            imageUrl={post.image_url!}
            altText="Post Image"
            onOpenLibrary={canEditMedia && handleOpenLibrary ? () => handleOpenLibrary(post.post_id, "image") : undefined}
            onUploadLocal={canEditMedia && handleSaveMedia ? (file) => handleSaveMedia(post.post_id, "image", file) : undefined}
            onRemove={
              canEditMedia && handleRemoveMedia && post.image_url
                ? () => handleRemoveMedia(post.post_id, "image")
                : undefined
            }
            isUploading={isUploading}
          />
        )}
        
        {/* Format selection moved to top */}
      </div>
      {/* Content Section — extra end padding on md+ so title/actions clear the selection checkbox */}
      <div className="flex flex-grow flex-col space-y-5 p-4 md:w-2/3 md:pr-14">
        {/* Title */}
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted">
              Title
            </span>
            {editableTitlePostId !== post.post_id && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted transition-colors hover:text-brand-primary"
                onClick={() => {
                  setEditableTitlePostId(post.post_id);
                  setEditableTitle(post.title || "");
                }}
              >
                <span className="material-icons text-sm">edit</span>
                Edit
              </button>
            )}
          </div>
          {editableTitlePostId === post.post_id ? (
            <div className="rounded-lg border border-theme-border bg-theme-background p-3">
              <input
                className="w-full rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-3 py-2 text-sm text-theme-fg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary-light/40"
                value={editableTitle || ""}
                onChange={(e) => setEditableTitle(e.target.value)}
                autoFocus
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-theme-border bg-theme-surface px-3 py-1.5 text-xs font-medium text-theme-fg shadow-sm hover:bg-theme-secondary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setEditableTitlePostId(null)}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() =>
                    handleSaveTitle(post.post_id, post.customer_id)
                  }
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-theme-border bg-theme-background px-4 py-3 text-sm font-medium text-theme-fg">
              {post.title || "No title"}
            </div>
          )}
        </div>

        {/* Scheduled / planned time */}
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted">
              {isPending ? "Planned time" : "Scheduled time"}
            </span>
            {editablePostId !== post.post_id && (
              <button
                type="button"
                className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide transition-colors ${
                  scheduleLockedPendingApproval || scheduleLockedTikTok
                    ? "cursor-not-allowed text-theme-fg-muted/50"
                    : "text-theme-fg-muted hover:text-brand-primary"
                }`}
                disabled={scheduleLockedPendingApproval || scheduleLockedTikTok}
                title={
                  scheduleLockedPendingApproval
                    ? "Approve this post before scheduling"
                    : scheduleLockedTikTok
                    ? `Complete the TikTok settings before scheduling: ${tiktokProblems[0]}`
                    : isPending
                    ? "Set the planned time"
                    : "Edit the scheduled time"
                }
                onClick={() => {
                  setEditablePostId(post.post_id);
                  setEditableTime(
                    post.scheduled_time ? new Date(post.scheduled_time) : null
                  );
                }}
              >
                <span className="material-icons text-sm">edit</span>
                Edit
              </button>
            )}
          </div>
          {scheduleLockedTikTok && (
            <div className="mb-1.5 flex items-center gap-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-theme-fg">
              <span className="material-icons text-sm text-amber-600">info</span>
              Complete the TikTok settings above before scheduling.
            </div>
          )}
          {editablePostId === post.post_id ? (
            <div className="rounded-lg border border-theme-border bg-theme-background p-3">
              <ScheduleDateTimeField
                autoOpenOnMount
                // The field now speaks absolute instants, so the old
                // getTimezoneOffset() round-trip through a "local" ISO string
                // is gone — that arithmetic could only ever mean browser time.
                value={
                  editableTime && !Number.isNaN(editableTime.getTime())
                    ? editableTime.toISOString()
                    : ""
                }
                onChange={(value) => {
                  if (!value) {
                    setEditableTime(null);
                    return;
                  }
                  const parsed = new Date(value);
                  setEditableTime(Number.isNaN(parsed.getTime()) ? null : parsed);
                }}
                timezone={editTimezone}
                onTimezoneChange={(tz) => {
                  userAdjustedTimezoneRef.current = true;
                  setEditTimezone(tz);
                }}
                timezoneLabel="Timezone"
                planned={isPending}
              />
              {isTikTokPost && (
                <div className="mt-2">
                  <TikTokConsentNotice options={post.tiktok_options} />
                </div>
              )}
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-theme-border bg-theme-surface px-3 py-1.5 text-xs font-medium text-theme-fg shadow-sm hover:bg-theme-secondary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => {
                    setEditablePostId(null);
                    setEditableTime(null);
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() =>
                    handleSaveTime(post.post_id, editableTime, editTimezone)
                  }
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-theme-border bg-theme-background px-4 py-3 text-sm text-theme-fg">
              <div className="font-medium text-theme-fg">
                {formatScheduledDisplay(post.scheduled_time)}
              </div>
              {post.scheduled_time && (
                <div className="mt-0.5 text-xs text-theme-fg-muted">
                  {formatTimezoneCity(displayTimezone)}
                </div>
              )}
              {plannedTimePassed && (
                <div className="mt-1 text-xs text-theme-fg-muted">
                  Planned time has passed — you&apos;ll be asked to publish now or pick a new time when approving.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Platform and YouTube Settings Section */}
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-theme-border bg-theme-background px-3 py-2 text-sm text-theme-fg-muted">
          <span className="font-medium text-theme-fg">{`Platform: ${post.platform}`}</span>

          {/* YouTube Privacy and COPPA Settings - Only show for YouTube platforms */}
          {(post.platform.toUpperCase() === "YOUTUBE" ||
            post.platform.toUpperCase() === "YOUTUBE_SHORTS") && (
            <>
              {/* Privacy Dropdown */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-theme-fg-muted">
                  Privacy: <span className="text-red-500">*</span>
                </label>
                <select
                  className="rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-2 py-1 text-sm text-theme-fg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary-light/40"
                  value={post.privacy_status || ""}
                  onChange={(e) => {
                    if (handleSavePrivacy && e.target.value) {
                      handleSavePrivacy(post.post_id, e.target.value);
                    }
                  }}
                  required
                >
                  <option value="">Select privacy mode</option>
                  <option value="public">Public</option>
                  <option value="unlisted">Unlisted</option>
                  <option value="private">Private</option>
                </select>
              </div>

              {/* Made for Kids - REQUIRED by YouTube API */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-theme-fg-muted">
                  Audience: <span className="text-red-500">*</span>
                </label>
                <select
                  className="rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-2 py-1 text-sm text-theme-fg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary-light/40"
                  value={
                    post.made_for_kids !== undefined
                      ? post.made_for_kids.toString()
                      : ""
                  }
                  onChange={(e) => {
                    if (handleSaveMadeForKids && e.target.value !== "") {
                      handleSaveMadeForKids(
                        post.post_id,
                        e.target.value === "true"
                      );
                    }
                  }}
                  required
                >
                  <option value="">Select audience</option>
                  <option value="false">Not Made for Kids</option>
                  <option value="true">Made for Kids</option>
                </select>
              </div>
            </>
          )}

          {/* TikTok Direct Post settings (SGL-170) */}
          {isTikTokPost && handleSaveTikTokOptions && (
            <TikTokPostSettings
              postId={post.post_id}
              options={post.tiktok_options}
              creatorInfo={tiktokCreator.creatorInfo}
              status={tiktokCreator.status}
              statusError={tiktokCreator.error}
              videoUrl={post.video_url || undefined}
              problems={tiktokProblems}
              disabled={isPublished || isSentToTikTok || isSaving || isTikTokProcessing || publishingPostId === post.post_id}
              onChange={(next) => void handleSaveTikTokOptions(post.post_id, next)}
              onVideoDuration={setTiktokVideoDuration}
            />
          )}
        </div>

        {/* Caption Section */}
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted">
              {captionLabel}
            </span>
            {editableCaptionPostId !== post.post_id &&
              regeneratingPostId !== post.post_id && (
                <div className="flex flex-shrink-0 items-center gap-0.5">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted transition-colors hover:bg-theme-secondary/15 hover:text-brand-primary disabled:opacity-50"
                    title={`Regenerate ${captionLabel}`}
                    onClick={() =>
                      onRegenerateCaption && onRegenerateCaption(post)
                    }
                    disabled={regeneratingPostId === post.post_id}
                  >
                    <span className="material-icons text-sm">autorenew</span>
                    Regenerate
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted transition-colors hover:bg-theme-secondary/15 hover:text-brand-primary"
                    onClick={() => {
                      setEditableCaptionPostId(post.post_id);
                      setEditableContent(post.content || "");
                    }}
                  >
                    <span className="material-icons text-sm">edit</span>
                    Edit
                  </button>
                </div>
              )}
          </div>
          {editableCaptionPostId === post.post_id ? (
            <div className="rounded-lg border border-theme-border bg-theme-background p-3">
              <textarea
                className="w-full rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-3 py-2 text-sm text-theme-fg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary-light/40"
                value={editableContent || ""}
                onChange={(e) => setEditableContent(e.target.value)}
                rows={6}
              />
              <div className="mt-3 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted transition-colors hover:bg-theme-secondary/15 hover:text-brand-primary disabled:opacity-50"
                  title={`Regenerate ${captionLabel}`}
                  onClick={() =>
                    onRegenerateCaption && onRegenerateCaption(post)
                  }
                  disabled={regeneratingPostId === post.post_id}
                >
                  {regeneratingPostId === post.post_id ? (
                    <span className="animate-spin material-icons text-base">
                      autorenew
                    </span>
                  ) : (
                    <>
                      <span className="material-icons text-base">
                        autorenew
                      </span>
                      Regenerate
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-theme-border bg-theme-surface px-3 py-1.5 text-xs font-medium text-theme-fg shadow-sm hover:bg-theme-secondary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setEditableCaptionPostId(null)}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleSaveCaption(post.post_id)}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-theme-border bg-theme-background px-4 py-3">
              {regeneratingPostId === post.post_id ? (
                <div className="flex items-center justify-center py-2">
                  <span className="animate-pulse text-sm text-brand-primary">
                    Regenerating {captionLabelLower}...
                  </span>
                </div>
              ) : regenerationSuccess === post.post_id ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
                  <span className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-emerald-600">
                    <span className="material-icons text-base">check_circle</span>
                    {captionLabel} successfully regenerated!
                  </span>
                  <span className="whitespace-pre-wrap text-sm text-theme-fg">
                    {post.content}
                  </span>
                </div>
              ) : (
                <div className="whitespace-pre-wrap text-sm text-theme-fg">
                  {post.content || (
                    <span className="text-theme-fg-muted">No {captionLabelLower}</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Hashtags Section */}
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted">
              Hashtags
            </span>
            {editableHashtagsPostId !== post.post_id && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted transition-colors hover:text-brand-primary"
                onClick={() => {
                  setEditableHashtagsPostId(post.post_id);
                  setEditableHashtags(post.hashtags || "");
                }}
              >
                <span className="material-icons text-sm">edit</span>
                Edit
              </button>
            )}
          </div>
          {editableHashtagsPostId === post.post_id ? (
            <div className="rounded-lg border border-theme-border bg-theme-background p-3">
              <textarea
                className="w-full rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-3 py-2 text-sm text-theme-fg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary-light/40"
                value={editableHashtags || ""}
                onChange={(e) => setEditableHashtags(e.target.value)}
                rows={2}
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-theme-border bg-theme-surface px-3 py-1.5 text-xs font-medium text-theme-fg shadow-sm hover:bg-theme-secondary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setEditableHashtagsPostId(null)}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleSaveHashtags(post.post_id)}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-theme-border bg-theme-background px-4 py-3">
              {hashtagTagsForDisplay.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {hashtagTagsForDisplay.map((tag, i) => (
                    <span
                      key={`${tag}-${i}`}
                      className="inline-flex rounded-full border border-theme-border/60 bg-theme-border/30 px-2.5 py-0.5 text-xs font-medium text-theme-fg"
                    >
                      {tag.startsWith("#") ? tag : `#${tag}`}
                    </span>
                  ))}
                </div>
              ) : (
                <span className="text-sm text-theme-fg-muted">No hashtags</span>
              )}
            </div>
          )}
        </div>
        {/* Call to Action Section */}
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted">
              Call to action
            </span>
            {editableCallToActionPostId !== post.post_id && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted transition-colors hover:text-brand-primary"
                onClick={() => {
                  setEditableCallToActionPostId(post.post_id);
                  setEditableCallToAction(post.call_to_action || "");
                }}
              >
                <span className="material-icons text-sm">edit</span>
                Edit
              </button>
            )}
          </div>
          {editableCallToActionPostId === post.post_id ? (
            <div className="rounded-lg border border-theme-border bg-theme-background p-3">
              <input
                className="w-full rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-3 py-2 text-sm text-theme-fg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary-light/40"
                value={editableCallToAction || ""}
                onChange={(e) => setEditableCallToAction(e.target.value)}
                autoFocus
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-theme-border bg-theme-surface px-3 py-1.5 text-xs font-medium text-theme-fg shadow-sm hover:bg-theme-secondary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setEditableCallToActionPostId(null)}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleSaveCallToAction(post.post_id)}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-theme-border bg-theme-background px-4 py-3 text-sm text-theme-fg">
              {post.call_to_action || (
                <span className="text-theme-fg-muted">No call to action</span>
              )}
            </div>
          )}
        </div>
        {/* Business Profile ID Section */}
        <div>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted">
              {getAccountIdFieldLabel(post.platform)}
            </span>
            {!showLinkedInTargetToggle && editableBusinessProfileIdPostId !== post.post_id && (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-theme-fg-muted transition-colors hover:text-brand-primary"
                onClick={() => {
                  setEditableBusinessProfileIdPostId(post.post_id);
                  setEditableBusinessProfileId(post.business_profile_id || "");
                }}
              >
                <span className="material-icons text-sm">edit</span>
                Edit
              </button>
            )}
          </div>
          {showLinkedInTargetToggle ? (
            <div className="flex gap-2 rounded-xl bg-theme-border/20 p-1">
              {(["personal", "business"] as const).map((t) => {
                const connected = t === "personal" ? linkedInTargets.personal : linkedInTargets.business;
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={!connected || isSaving}
                    title={connected ? undefined : "Connect this LinkedIn account in Connections to use it."}
                    onClick={() => {
                      const resolved = mapLinkedInPlatform(post.platform, connectedProfiles, t);
                      handleSaveBusinessProfileId(post.post_id, resolved.business_profile_id);
                    }}
                    className={`flex-1 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                      effectiveLinkedInTarget === t
                        ? "bg-brand-primary text-white shadow-sm"
                        : "bg-theme-surface text-theme-fg-muted hover:bg-theme-background"
                    }`}
                  >
                    {t === "personal" ? "Personal" : "Business Page"}
                  </button>
                );
              })}
            </div>
          ) : editableBusinessProfileIdPostId === post.post_id ? (
            <div className="rounded-lg border border-theme-border bg-theme-background p-3">
              <input
                type="text"
                className="w-full rounded-md border border-[var(--color-input-border)] bg-[var(--color-input-bg)] px-3 py-2 text-sm text-theme-fg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary-light/40"
                value={editableBusinessProfileId || ""}
                onChange={(e) => setEditableBusinessProfileId(e.target.value)}
                placeholder={`Enter ${getAccountIdFieldLabel(post.platform)}`}
                autoFocus
              />
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-theme-border bg-theme-surface px-3 py-1.5 text-xs font-medium text-theme-fg shadow-sm hover:bg-theme-secondary/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setEditableBusinessProfileIdPostId(null)}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded-lg bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleSaveBusinessProfileId(post.post_id)}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-theme-border bg-theme-background px-4 py-3">
              <span className="mt-0.5 inline-block h-4 w-1 shrink-0 rounded bg-brand-primary" />
              <span className="break-all text-sm text-theme-fg">
                {isDefaultBusinessProfileId(post.business_profile_id) ? (
                  <span className="text-theme-fg-muted">
                    No {getPlatformDisplayName(post.platform)} account connected.
                  </span>
                ) : post.business_profile_id ? (
                  post.business_profile_id
                ) : (
                  <span className="text-theme-fg-muted">Not set</span>
                )}
              </span>
            </div>
          )}
        </div>
        {/* Separator */}
        <hr className="my-2 border-t border-theme-border" />
        {isTikTokPost && !isPublished && !isSentToTikTok && (
          <TikTokConsentNotice options={post.tiktok_options} />
        )}
        {/* Buttons and Status Section */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white shadow-md shadow-[#2f4941]/25 transition-colors hover:bg-brand-primary-hover disabled:opacity-50"
              onClick={() => handleApprove(post.post_id)}
              disabled={approvingPostId === post.post_id || isApproved || isSentToTikTok}
              title={
                isSentToTikTok
                  ? TIKTOK_INBOX_APPROVE_TOOLTIP
                  : isApproved
                  ? "This post is approved"
                  : "Approve this post"
              }
            >
              <span className={`material-icons text-xl ${approvingPostId === post.post_id ? "animate-spin" : ""}`}>
                {approvingPostId === post.post_id ? "autorenew" : "check_circle"}
              </span>
              {approvingPostId === post.post_id
                ? "Approving..."
                : isApproved
                ? "Approved"
                : "Approve"}
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed ${
                !isApproved && !isPublished ? "disabled:opacity-50" : ""
              } ${
                publishingPostId === post.post_id
                  ? "cursor-wait border-theme-border bg-theme-background text-theme-fg-muted"
                  : isPublished
                  ? "border-emerald-600/35 bg-emerald-500/10 text-theme-fg hover:bg-emerald-500/15"
                  : "border-brand-primary-light/50 bg-brand-primary-light/10 text-brand-primary-dark hover:bg-brand-primary-light/20"
              }`}
              onClick={() => handlePublishPost(post.post_id, post.customer_id)}
              disabled={publishDisabled}
              title={
                isPublished
                  ? "Published"
                  : isTikTokProcessing
                  ? "TikTok is processing your video…"
                  : !isApproved
                  ? "Approve the post before publishing"
                  : missingYouTubeRequiredFields
                  ? "Set Privacy and Audience before publishing to YouTube"
                  : missingTikTokFields
                  ? tiktokProblems[0]
                  : "Publish this post"
              }
            >
              <span className="material-icons text-xl">send</span>
              {publishingPostId === post.post_id
                ? "Publishing..."
                : isPublished
                ? "Published!"
                : isSentToTikTok
                ? "Sent to TikTok"
                : "Publish"}
            </button>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-theme-border bg-theme-background text-red-600 transition-colors hover:bg-red-500/10 hover:text-red-700"
              title="Delete"
              onClick={() => handleDeletePost(post.post_id, post.customer_id)}
            >
              <span className="material-icons text-xl">delete</span>
            </button>
          </div>
          <div className="flex flex-col items-end">
            <div
              className={`text-xs font-semibold px-2 py-1 rounded-md ${
                statusUpper === "PENDING"
                  ? "bg-yellow-100 text-yellow-800"
                  : statusUpper === "APPROVED"
                  ? "bg-green-100 text-green-800"
                  : statusUpper === "PUBLISHED"
                  ? "bg-blue-100 text-blue-800"
                  : isSentToTikTok
                  ? "bg-sky-100 text-sky-800"
                  : "bg-theme-border/40 text-theme-fg"
              }`}
            >
              {getPostStatusLabelForUi(post.status)}
              {(isScheduledApproved || isPlannedPending) && formatTimezoneCity(displayTimezone) && (
                <div className="font-normal text-[10px] opacity-70 mt-0.5">
                  {formatTimezoneCity(displayTimezone)}
                </div>
              )}
            </div>
            {publishError[post.post_id] && (
              <span className="text-xs text-red-600 mt-1">
                {publishError[post.post_id]}
              </span>
            )}
            {isTikTokPost && isSentToTikTok && (
              <span className="text-xs text-sky-800 mt-1 max-w-xs text-right">
                {TIKTOK_INBOX_MESSAGE}
              </span>
            )}
            {isTikTokProcessing && (
              <span className="text-xs text-theme-fg-muted mt-1 max-w-xs text-right">
                TikTok is processing your video…
              </span>
            )}
            {showTikTokProcessingNote && (
              <span className="text-xs text-theme-fg-muted mt-1 max-w-xs text-right">
                {TIKTOK_PROCESSING_MESSAGE}
              </span>
            )}
            {!publishError[post.post_id] && missingYouTubeRequiredFields && (
              <span className="text-xs text-amber-700 mt-1">
                Set Privacy and Audience before publishing to YouTube.
              </span>
            )}
            {post.platform_post_id && (
              <LivePostLink
                platform={post.platform}
                platformPostId={String(post.platform_post_id ?? "")}
                business_profile_id={post.business_profile_id}
                platform_response={post.platform_response}
                isDocumentPost={isPdfCarousel || hasDocumentAttachment}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostPreviewCardFull;
